import { z } from "zod";

// The one shared literal the state schema pins directly, so a receipt in state
// and a receipt from the NL-014 builder cannot claim different rounding rules.
import { DECIMAL_ROUNDING_CONTRACT_VERSION } from "@/lib/calculator-contracts/decimal";

export const FUNNEL_TEMPLATE_IDS = [
  "agency-white-label",
  "franchise-multi-location",
  "ai-search-geo",
  "adult-compliance",
  "b2b-enterprise",
  "ecommerce-dtc",
  "high-ticket-coaching",
  "saas-plg",
] as const;
export type FunnelTemplateId = (typeof FUNNEL_TEMPLATE_IDS)[number];

export const ALL_APP_LOCALES = [
  "en", "ar", "de", "es", "fr", "he", "hi", "id", "it",
  "ja", "ko", "pt-br", "ru", "tr", "zh-cn",
] as const;
export type AppLocale = (typeof ALL_APP_LOCALES)[number];

export { type FitAudience, type FitBand } from "@/lib/fit-band/engine";

export const CURRENT_VERSION = 7;

export const FUNNEL_STATE_VERSIONS = [3, 4, 5, 6, 7] as const;
export type FunnelStateVersion = (typeof FUNNEL_STATE_VERSIONS)[number];

export const FunnelStateVersionSchema = z.union([
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
]);

// Regional planning
export const FUNNEL_CURRENCY_CODES = [
  "USD", "EUR", "GBP", "ILS", "CAD", "AUD", "NZD", "INR", "BRL", "MXN",
  "JPY", "KRW", "CNY", "SGD", "HKD", "CHF", "SEK", "NOK", "DKK", "PLN",
  "CZK", "HUF", "TRY", "ZAR", "AED", "SAR", "IDR", "THB", "PHP",
] as const;
export type FunnelCurrencyCode = (typeof FUNNEL_CURRENCY_CODES)[number];
export const FUNNEL_CURRENCIES = FUNNEL_CURRENCY_CODES;
export type FunnelCurrency = FunnelCurrencyCode;

export const FUNNEL_REGION_IDS = [
  "US", "EU", "UK", "IL", "CA", "AU", "NZ", "IN", "BR", "MX",
  "JP", "KR", "CN", "SG", "AE", "OTHER",
  "us", "canada", "uk", "eu", "israel", "australia", "india", "brazil", "japan", "global",
] as const;
export type FunnelRegionId = (typeof FUNNEL_REGION_IDS)[number];
export const REGIONS = FUNNEL_REGION_IDS;
export type FunnelRegion = FunnelRegionId;

export const DEFAULT_REGIONAL_SETTINGS = {
  region: "US",
  countryCode: "US",
  currency: "USD",
  locale: "en",
  taxRatePercent: 0,
  shippingCostPerOrder: 0,
  fulfillmentCostPercent: 0,
  defaultMerchantFeePercent: 2.9,
  defaultMerchantFixedFee: 0.3,
  assumptionsLabel: "benchmark_default",
} as const;

export const RegionalSettingsSchema = z.object({
  region: z.enum(FUNNEL_REGION_IDS).default(DEFAULT_REGIONAL_SETTINGS.region),
  countryCode: z.string().min(2).max(3).default(DEFAULT_REGIONAL_SETTINGS.countryCode),
  currency: z.enum(FUNNEL_CURRENCY_CODES).default(DEFAULT_REGIONAL_SETTINGS.currency),
  locale: z.enum(ALL_APP_LOCALES).default(DEFAULT_REGIONAL_SETTINGS.locale),
  taxRatePercent: z.number().min(0).max(100).default(DEFAULT_REGIONAL_SETTINGS.taxRatePercent),
  shippingCostPerOrder: z.number().min(0).max(100_000).default(DEFAULT_REGIONAL_SETTINGS.shippingCostPerOrder),
  fulfillmentCostPercent: z.number().min(0).max(100).default(DEFAULT_REGIONAL_SETTINGS.fulfillmentCostPercent),
  defaultMerchantFeePercent: z.number().min(0).max(15).default(DEFAULT_REGIONAL_SETTINGS.defaultMerchantFeePercent),
  defaultMerchantFixedFee: z.number().min(0).max(100).default(DEFAULT_REGIONAL_SETTINGS.defaultMerchantFixedFee),
  assumptionsLabel: z.enum(["benchmark_default", "user_input", "calculated", "estimated"])
    .default(DEFAULT_REGIONAL_SETTINGS.assumptionsLabel),
});
export type RegionalSettings = z.infer<typeof RegionalSettingsSchema>;

// Audience and goal details
export const OBJECTIVE_TYPES = [
  "profit", "revenue", "leads", "customers", "booked_calls", "cac", "payback", "capacity", "awareness",
] as const;
export type ObjectiveType = (typeof OBJECTIVE_TYPES)[number];

export const RISK_TOLERANCE_LEVELS = ["low", "medium", "high"] as const;
export type RiskToleranceLevel = (typeof RISK_TOLERANCE_LEVELS)[number];

export const SALES_MOTIONS = [
  "self_serve", "sales_assisted", "high_ticket_call", "product_led", "partner_led", "hybrid",
] as const;
export type SalesMotion = (typeof SALES_MOTIONS)[number];

export const AudienceGoalDetailsSchema = z.object({
  icp: z.string().max(280).optional(),
  icpSegment: z.string().max(160).optional(),
  buyingTrigger: z.string().max(220).optional(),
  primaryObjection: z.string().max(220).optional(),
  mainObjection: z.string().max(160).optional(),
  decisionMaker: z.string().max(120).optional(),
  objectiveType: z.enum(OBJECTIVE_TYPES).optional(),
  objectiveValue: z.number().min(0).max(1_000_000_000).optional(),
  timeframeMonths: z.number().int().min(1).max(60).optional(),
  budgetCeiling: z.number().min(0).max(100_000_000).optional(),
  riskTolerance: z.enum(RISK_TOLERANCE_LEVELS).optional(),
  salesMotion: z.enum(SALES_MOTIONS).optional(),
  complianceCategory: z.string().max(80).optional(),
});
export type AudienceGoalDetails = z.infer<typeof AudienceGoalDetailsSchema>;

// Traffic Channel
export const TRAFFIC_CHANNEL_IDS = [
  "google_organic", "bing_organic", "other_search", "yahoo_organic", "yandex_organic",
  "google_ads", "bing_ads", "ppc_source", "yahoo_ads", "yandex_ads",
  "facebook_organic", "instagram", "linkedin", "twitter", "pinterest",
  "tiktok", "snapchat", "youtube", "vimeo", "vk_organic", "whatsapp",
  "facebook_ads", "linkedin_ads", "twitter_ads", "pinterest_ads", "tiktok_ads",
  "snapchat_ads", "vk_ads", "whatsapp_ads", "youtube_ads", "video_ads",
  "email_newsletter", "email_ads",
  "sms", "blog_articles", "podcast", "press_release",
  "affiliates", "jv_partners", "referral", "cpa_network",
  "display_ads", "media_buys", "ppv_source",
  "direct_mail", "magazine_ad", "newspaper_ad", "radio_ad", "tv_ad",
  "retargeting", "retargeting_meta", "reddit_organic", "quora_organic", "skool",
  "reddit_ads", "quora_ads", "amazon_ads", "apple_search_ads", "ctv_ott",
  "taboola", "dsp", "review_sites", "aso",
  "amazon_dsp", "walmart_connect", "target_roundel", "instacart_ads",
  "chatgpt_search", "perplexity_ai", "gemini_search", "bing_copilot",
  "custom",
] as const;
export type TrafficChannelId = (typeof TRAFFIC_CHANNEL_IDS)[number];

export const ACQUISITION_COST_MODELS = [
  "organic", "cpc", "cpm", "cpv", "flat_fee", "cpa",
  "cost_per_lead", "revenue_share", "labor_time", "organic_unknown",
] as const;
export type AcquisitionCostModel = (typeof ACQUISITION_COST_MODELS)[number];

export const TrafficChannelSchema = z.object({
  id: z.string().min(1).max(40),
  label: z.string().min(1).max(60),
  monthlyVisitors: z.number().int().min(0).max(10_000_000).default(0),
  acquisitionType: z.enum([
    "organic", "cpc", "cpm", "cpv", "flat_fee", "cpa",
    "cost_per_lead", "revenue_share", "labor_time", "organic_unknown",
  ]).default("organic"),
  cpc: z.number().min(0).optional(),
  costPerView: z.number().min(0).optional(),
  cpm: z.number().min(0).optional(),
  ctr: z.number().min(0).max(100).optional(),
  flatMonthlyFee: z.number().min(0).optional(),
  costPerLead: z.number().min(0).optional(),
  costPerAcquisition: z.number().min(0).optional(),
  revenueSharePercent: z.number().min(0).max(100).optional(),
  laborHoursPerMonth: z.number().min(0).optional(),
  hourlyCost: z.number().min(0).optional(),
  campaignName: z.string().max(120).optional(),
  intentLevel: z.enum(["cold", "warm", "hot"]).optional(),
  funnelPosition: z.enum(["top", "middle", "bottom", "retargeting"]).optional(),
  trackingConfidencePct: z.number().min(0).max(100).optional(),
  utmSource: z.string().max(80).optional(),
  utmMedium: z.string().max(80).optional(),
  utmCampaign: z.string().max(120).optional(),
  utmContent: z.string().max(120).optional(),
  utmTerm: z.string().max(120).optional(),
  attributionRole: z.enum(["first_touch", "last_touch", "assist", "retargeting", "direct"]).default("first_touch").optional(),
  trackingSetup: z.enum(["none", "utm_only", "pixel", "server_side", "crm_matched"]).default("utm_only").optional(),
  audienceTemperature: z.enum(["cold", "warm", "hot"]).optional(),
  geo: z.string().max(80).optional(),
  device: z.enum(["all", "desktop", "mobile", "tablet"]).default("all").optional(),
  language: z.string().max(20).optional(),
  monthlyBudgetCap: z.number().min(0).optional(),
  rampType: z.enum(["flat", "linear", "seasonal", "launch_spike", "decay"]).default("flat").optional(),
  confidence: z.enum(["low", "medium", "high"]).default("medium").optional(),
  retargetingSourceStageId: z.string().max(80).optional(),
});
export type TrafficChannel = z.infer<typeof TrafficChannelSchema>;

// Stage Config
export const STAGE_TYPES = [
  "blog-post",
  "landing-page",
  "quiz-page",
  "advertorial",
  "calculator-page",
  "waitlist-page",
  "opt-in-page",
  "email-sequence",
  "vsl-page",
  "webinar-registration",
  "webinar-live",
  "webinar-replay",
  "podcast",
  "challenge-page",
  "sales-page",
  "order-form",
  "free-plus-shipping",
  "tripwire-page",
  "pricing-page",
  "free-trial",
  "upsell",
  "downsell",
  "order-bump",
  "product-detail-page",
  "cart-page",
  "application-page",
  "booking-page",
  "discovery-call",
  "proposal",
  "case-study",
  "in-app-upgrade",
  "usage-limit-gate",
  "cancellation-save",
  "referral-dashboard",
  "thank-you-page",
  "activation-event",
  "onboarding-checklist",
  "community-join",
  "retainer-close",
  "affiliate-signup",
  "share-page",
  "retargeting-loop",
  "wait-delay",
  "survey-split",
  "custom",
] as const;
export type StageType = (typeof STAGE_TYPES)[number];

export const FUNNEL_ASSET_TYPES = [
  "page", "video", "email", "sms", "call_script", "ad", "form", "checkout", "report", "other",
] as const;
export type FunnelAssetType = (typeof FUNNEL_ASSET_TYPES)[number];

export const FUNNEL_ASSET_SOURCE_SYSTEMS = [
  "seoh", "client_site", "cms", "crm", "posthog", "gtm", "voomly", "doodly", "clickfunnels", "other",
] as const;
export type FunnelAssetSourceSystem = (typeof FUNNEL_ASSET_SOURCE_SYSTEMS)[number];

export const FUNNEL_ASSET_EXECUTION_STATUSES = [
  "unassigned", "modeled", "briefed", "built", "published", "measured", "needs_fix",
] as const;
export type FunnelAssetExecutionStatus = (typeof FUNNEL_ASSET_EXECUTION_STATUSES)[number];

export const FunnelStageConfigSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(40),
  conversionRate: z.number().min(0.1).max(100),
  isEditable: z.boolean().default(true),
  stageType: z.string().max(40).default("custom"),
  note: z.string().max(280).optional(),
  url: z.string().max(500).optional(),
  color: z.string().max(20).optional(),
  boxWidth: z.number().min(120).max(800).optional(),
  boxHeight: z.number().min(64).max(420).optional(),
  publishedStatus: z.enum(["draft", "live", "paused"]).default("draft"),
  actualCvr: z.number().min(0).max(100).optional(),
  actualVolume: z.number().int().min(0).optional(),
  actualPeriodStart: z.string().max(20).optional(),
  actualPeriodEnd: z.string().max(20).optional(),
  actualPeriodDays: z.number().int().min(1).max(366).optional(),
  requiredStage: z.boolean().default(true).optional(),
  owner: z.string().max(80).optional(),
  assetStatus: z.enum(["idea", "draft", "live", "needs_fix", "paused"]).default("draft").optional(),
  assetUrl: z.string().max(500).optional(),
  assetType: z.enum(FUNNEL_ASSET_TYPES).optional(),
  assetSourceSystem: z.enum(FUNNEL_ASSET_SOURCE_SYSTEMS).optional(),
  assetThumbnailUrl: z.string().max(500).optional(),
  assetExecutionStatus: z.enum(FUNNEL_ASSET_EXECUTION_STATUSES).default("unassigned").optional(),
  assetCtaNotes: z.string().max(280).optional(),
  trackingStatus: z.enum(["not_set", "none", "planned", "utm_only", "pixel", "installed", "server_side", "crm_matched", "verified", "broken"]).default("not_set").optional(),
  waitDays: z.number().int().min(0).max(365).default(0).optional(),
  dueInDays: z.number().int().min(0).max(365).optional(),
  priority: z.enum(["low", "medium", "high"]).default("medium").optional(),
  confidencePct: z.number().min(0).max(100).optional(),
});
export type FunnelStageConfig = z.infer<typeof FunnelStageConfigSchema>;

// Projection Inputs
export const DEFAULT_PROJECTION_MONTHS = 12;
export const MIN_PROJECTION_MONTHS = 1;
export const MAX_PROJECTION_MONTHS = 36;

export const ProjectionInputsSchema = z.object({
  monthlyVisitors: z.number().int().min(1).max(10_000_000).default(1_000),
  revenuePerLead: z.number().min(1).max(500_000).default(2_000),
  projectionMonths: z.number().int().min(MIN_PROJECTION_MONTHS).max(MAX_PROJECTION_MONTHS).default(DEFAULT_PROJECTION_MONTHS),
  reinvestmentPercent: z.number().min(0).max(100).default(0).optional(),
  reinvestmentCap: z.number().min(0).optional(),
});
export type ProjectionInputs = z.infer<typeof ProjectionInputsSchema>;

export const FUNNEL_EXPENSE_CATEGORIES = [
  "seo_content", "development", "tracking", "creative", "compliance_review",
  "sales_ops", "hosting_tools", "contractor_labor", "general",
] as const;
export type FunnelExpenseCategory = (typeof FUNNEL_EXPENSE_CATEGORIES)[number];

export const FUNNEL_EXPENSE_FREQUENCIES = [
  "one_time", "monthly", "quarterly", "annual",
] as const;
export type FunnelExpenseFrequency = (typeof FUNNEL_EXPENSE_FREQUENCIES)[number];

export const FunnelExpenseSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(120),
  category: z.enum(FUNNEL_EXPENSE_CATEGORIES).default("general"),
  amount: z.number().min(0).max(10_000_000).default(0),
  frequency: z.enum(FUNNEL_EXPENSE_FREQUENCIES).default("monthly"),
  startMonth: z.number().int().min(1).max(60).default(1).optional(),
  endMonth: z.number().int().min(1).max(60).optional(),
  stageId: z.string().max(80).optional(),
  channelId: z.string().max(80).optional(),
  productId: z.string().max(80).optional(),
  owner: z.string().max(80).optional(),
  notes: z.string().max(280).optional(),
});
export type FunnelExpense = z.infer<typeof FunnelExpenseSchema>;

// Contact
export const FunnelContactSchema = z.object({
  email: z.string().regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/).max(160),
  name: z.string().max(120).optional(),
  company: z.string().max(160).optional(),
  privacyNoticeAccepted: z.literal(true),
  /**
   * Marketing-email consent, captured by its own unticked checkbox and kept
   * strictly independent of `privacyNoticeAccepted`. Accepting the privacy
   * notice is a precondition for handling the plan at all; it is not and must
   * never be read as agreement to receive marketing. Optional so states saved
   * before this field existed still validate, and absent means `false`.
   */
  marketingEmailOptIn: z.boolean().optional(),
});
export type FunnelContact = z.infer<typeof FunnelContactSchema>;

// Product
export const PRODUCT_PRICE_TYPES = [
  "one_time", "monthly", "yearly", "usage_based", "seat_based", "payment_plan",
] as const;
export type ProductPriceType = (typeof PRODUCT_PRICE_TYPES)[number];

export const PRODUCT_TYPES = [
  "online_course", "saas", "membership", "digital_download", "coaching",
  "done_for_you", "agency_retainer", "physical_product", "supplement",
  "affiliate", "high_ticket", "low_ticket", "free_plus_shipping", "event",
  "paid_newsletter", "paid_community", "software_plugin", "mentorship",
  "subscription_box", "usage_based_api", "seat_based_saas", "mobile_app",
  "cohort_course", "franchise", "local_service", "ai_agent", "other",
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const ProductSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(80),
  productType: z.enum(PRODUCT_TYPES).default("other"),
  priceType: z.enum(PRODUCT_PRICE_TYPES).default("one_time"),
  price: z.number().min(0).max(1_000_000).default(997),
  cogsPercent: z.number().min(0).max(100).default(10),
  refundRatePercent: z.number().min(0).max(100).default(5),
  merchantFeePercent: z.number().min(0).max(10).default(2.9),
  churnRatePercent: z.number().min(0).max(100).default(5).optional(),
  monthsRetained: z.number().min(1).max(120).default(12).optional(),
  hasTrial: z.boolean().default(false),
  trialDays: z.number().min(1).max(365).default(14).optional(),
  trialToPaidCvrPct: z.number().min(0).max(100).default(30).optional(),
  paymentPlanMonths: z.number().min(2).max(36).optional(),
  paymentPlanDefaultRatePct: z.number().min(0).max(50).default(8).optional(),
  isUpsell: z.boolean().default(false),
  upsellTakeRatePct: z.number().min(0).max(100).optional(),
  usageBased: z.boolean().default(false),
  usageMetric: z.string().max(40).optional(),
  overageRateUsd: z.number().min(0).optional(),
  annualDiscountPct: z.number().min(0).max(80).default(20).optional(),
  guaranteeType: z.enum([
    "none", "money_back_30", "money_back_60", "satisfaction", "results",
  ]).default("none"),
  offerRole: z.enum(["core", "order_bump", "upsell", "downsell", "backend", "continuity", "application_only", "quote_only"]).default("core").optional(),
  attachedStageIds: z.array(z.string().max(80)).max(12).default([]).optional(),
  checkoutType: z.enum(["one_step", "two_step", "invoice", "subscription_checkout", "sales_call_close"]).default("one_step").optional(),
  productSalesSplitPct: z.number().min(0).max(100).default(100).optional(),
  orderBumpEnabled: z.boolean().default(false).optional(),
  orderBumpTakeRatePct: z.number().min(0).max(100).default(20).optional(),
  downsellTakeRatePct: z.number().min(0).max(100).default(12).optional(),
  merchantFixedFee: z.number().min(0).max(100).default(0.3).optional(),
  fulfillmentCostAmount: z.number().min(0).max(1_000_000).default(0).optional(),
  fulfillmentCostType: z.enum(["none", "one_time", "recurring", "per_order"]).default("none").optional(),
});
export type Product = z.infer<typeof ProductSchema>;

// Capacity Config
export const CapacityConfigSchema = z.object({
  maxCallsPerMonth: z.number().int().min(1).optional(),
  salesRepCount: z.number().int().min(1).default(1),
  hoursPerRepPerMonth: z.number().min(1).default(80),
  avgCallDurationMins: z.number().min(5).default(45),
  closeRateByRepPct: z.number().min(0).max(100).optional(),
}).optional();
export type CapacityConfig = z.infer<typeof CapacityConfigSchema>;

// Graph types for canvas (step 5)
export const FUNNEL_GRAPH_NODE_TYPES = [
  "trafficSource", "stage", "branch", "retargeting", "wait", "surveySplit",
] as const;
export type FunnelGraphNodeType = (typeof FUNNEL_GRAPH_NODE_TYPES)[number];

export const FUNNEL_EDGE_TYPES = ["direct", "branch", "retarget", "skip", "loop", "main", "yes", "no", "split", "optional", "retargeting", "wait", "survey"] as const;
export type FunnelEdgeType = (typeof FUNNEL_EDGE_TYPES)[number];

export const FunnelGraphNodeSchema = z.object({
  id: z.string().max(120),
  type: z.enum(FUNNEL_GRAPH_NODE_TYPES),
  position: z.object({ x: z.number(), y: z.number() }),
  data: z.object({
    label: z.string().max(120),
    stageType: z.string().max(60).default("custom"),
    conversionRate: z.number().min(0).max(100).default(10),
    channel: z.string().max(80).optional(),
    isSource: z.boolean().default(false),
    monthlyVisitors: z.number().optional(),
    url: z.string().max(500).optional(),
    publishedStatus: z.enum(["draft", "live", "paused"]).default("draft").optional(),
    owner: z.string().max(80).optional(),
    assetStatus: z.enum(["idea", "draft", "live", "needs_fix", "paused"]).default("draft").optional(),
    trackingStatus: z.enum(["not_set", "none", "planned", "utm_only", "pixel", "installed", "server_side", "crm_matched", "verified", "broken"]).default("not_set").optional(),
    waitDays: z.number().int().min(0).max(365).default(0).optional(),
    dueInDays: z.number().int().min(0).max(365).optional(),
    actualCvr: z.number().min(0).max(100).optional(),
    actualVolume: z.number().int().min(0).optional(),
    actualPeriodStart: z.string().max(20).optional(),
    actualPeriodEnd: z.string().max(20).optional(),
    actualPeriodDays: z.number().int().min(1).max(366).optional(),
    requiredStage: z.boolean().default(true).optional(),
    assetUrl: z.string().max(500).optional(),
    assetType: z.enum(FUNNEL_ASSET_TYPES).optional(),
    assetSourceSystem: z.enum(FUNNEL_ASSET_SOURCE_SYSTEMS).optional(),
    assetThumbnailUrl: z.string().max(500).optional(),
    assetExecutionStatus: z.enum(FUNNEL_ASSET_EXECUTION_STATUSES).default("unassigned").optional(),
    assetCtaNotes: z.string().max(280).optional(),
    color: z.string().max(20).optional(),
    boxWidth: z.number().min(120).max(800).optional(),
    boxHeight: z.number().min(64).max(420).optional(),
    splitPercent: z.number().min(0).max(100).optional(),
    branchLabel: z.string().max(80).optional(),
    sourceNodeId: z.string().max(80).optional(),
    productIds: z.array(z.string().min(1).max(40)).max(8).optional(),
    nodeKind: z.enum(["stage", "branch", "retargeting", "wait", "survey"]).default("stage").optional(),
    optionalStage: z.boolean().default(false).optional(),
  }),
});

export const FunnelGraphEdgeSchema = z.object({
  id: z.string().max(120),
  source: z.string().max(120),
  target: z.string().max(120),
  edgeType: z.enum(FUNNEL_EDGE_TYPES).default("direct").optional(),
  splitPercent: z.number().min(0).max(100).default(100).optional(),
  conversionRate: z.number().min(0).max(100).optional(),
  edgeCvr: z.number().min(0).max(100).optional(),
  waitDays: z.number().int().min(0).max(365).default(0).optional(),
  isRequired: z.boolean().optional(),
  label: z.string().max(80).optional(),
});

// Generous caps (real plans stay far below them) so a stored or posted graph
// cannot grow without bound.
export const FunnelGraphSchema = z.object({
  nodes: z.array(FunnelGraphNodeSchema).max(200),
  edges: z.array(FunnelGraphEdgeSchema).max(400),
});

export type FunnelGraphNode = z.infer<typeof FunnelGraphNodeSchema>;
export type FunnelGraphEdge = z.infer<typeof FunnelGraphEdgeSchema>;
export type FunnelGraph = z.infer<typeof FunnelGraphSchema>;

export const ScenarioSnapshotSchema = z.object({
  id: z.string().min(1).max(60),
  name: z.string().min(1).max(80),
  kind: z.enum(["baseline", "conservative", "optimistic", "custom"]).default("custom"),
  cvrMultiplier: z.number().min(0.1).max(3).default(1),
  visitorMultiplier: z.number().min(0.1).max(5).default(1),
  revenueMultiplier: z.number().min(0.1).max(5).default(1),
  changedAssumptions: z.array(z.string().max(120)).max(8).default([]),
  notes: z.string().max(280).optional(),
});
export type ScenarioSnapshot = z.infer<typeof ScenarioSnapshotSchema>;

export const ExperimentSchema = z.object({
  id: z.string().min(1).max(60),
  stageId: z.string().max(80),
  hypothesis: z.string().max(240),
  variantA: z.string().max(120).default("Control"),
  variantB: z.string().max(120).default("Variant"),
  trafficSplitPct: z.number().min(0).max(100).default(50),
  primaryKpi: z.enum(["cvr", "revenue", "leads", "customers", "booked_calls", "cac", "aov"]).default("cvr"),
  status: z.enum(["idea", "running", "paused", "winner", "lost"]).default("idea"),
  winner: z.enum(["a", "b", "none"]).default("none"),
  actualUpliftPct: z.number().min(-100).max(500).optional(),
});
export type FunnelExperiment = z.infer<typeof ExperimentSchema>;

/**
 * Version 7 session-only planning slots.
 *
 * These three fields let the *active* planning session explain an estimate.
 * They are never persisted, shared, exported, or submitted: every serialization
 * boundary routes through `toPersistablePlannerState`
 * (`lib/funnel-planner/session-fields.ts`), and `applyV7Defaults` resets them on
 * every load. A v3-v6 plan or an existing share link therefore migrates to v7
 * with all three inert, and can never gain v7 data.
 *
 * Nothing here authorizes a quote, invoice, checkout, revenue figure, CRM
 * record, or any other commercial action; the planner stays estimate-only.
 */

/**
 * Kept as a local tuple so `schema.ts` stays dependency-free apart from zod.
 * `session-fields.test.ts` pins it against `PACKAGE_RECOMMENDATION_TIERS` in
 * `lib/packages/package-recommender.ts` so the two cannot drift.
 */
export const FUNNEL_PACKAGE_TIERS = ["Starter", "Growth", "Enterprise"] as const;
export type FunnelPackageTier = (typeof FUNNEL_PACKAGE_TIERS)[number];

/** What the visitor did with the adviser's suggestion. */
export const PACKAGE_ADVISER_DECISIONS = ["none", "accepted", "overridden", "skipped"] as const;
export type PackageAdviserDecision = (typeof PACKAGE_ADVISER_DECISIONS)[number];

export const DEFAULT_PACKAGE_RECOMMENDATION = {
  enabled: false,
  decision: "none",
  recommendedPackageId: null,
  selectedPackageId: null,
  ruleVersion: null,
  pricingBand: null,
  pricingSnapshotVersion: null,
  manualQuoteRequired: false,
} as const;

export const PackageRecommendationSchema = z.object({
  enabled: z.boolean().default(false),
  decision: z.enum(PACKAGE_ADVISER_DECISIONS).default("none"),
  recommendedPackageId: z.enum(FUNNEL_PACKAGE_TIERS).nullable().default(null),
  selectedPackageId: z.enum(FUNNEL_PACKAGE_TIERS).nullable().default(null),
  ruleVersion: z.string().max(40).nullable().default(null),
  /** A display-ready indicative band string, never a committed price. */
  pricingBand: z.string().max(80).nullable().default(null),
  pricingSnapshotVersion: z.string().max(80).nullable().default(null),
  manualQuoteRequired: z.boolean().default(false),
}).strict();
export type PackageRecommendation = z.infer<typeof PackageRecommendationSchema>;

/**
 * Seasonal planning scenarios. The percentages are visitor-owned planning
 * assumptions, never external market evidence; source-backed profiles stay
 * disabled until an owner approves a real source.
 */
export const SEASONAL_PLANNING_SCENARIOS = ["lower", "expected", "upper"] as const;
export type SeasonalPlanningScenario = (typeof SEASONAL_PLANNING_SCENARIOS)[number];

export const SEASONAL_ASSUMPTION_SOURCES = ["none", "manual"] as const;
export type SeasonalAssumptionSource = (typeof SEASONAL_ASSUMPTION_SOURCES)[number];

/** Two-decimal fixed-point, matching the NL-010 disabled multiplier literal. */
const SEASONAL_MULTIPLIER_PATTERN = /^\d+\.\d{2}$/;

export const DEFAULT_SEASONAL_DEMAND = {
  enabled: false,
  scenario: null,
  multiplier: "1.00",
  assumptionSource: "none",
  lowerPercent: null,
  expectedPercent: null,
  upperPercent: null,
} as const;

export const SeasonalDemandSchema = z.object({
  enabled: z.boolean().default(false),
  scenario: z.enum(SEASONAL_PLANNING_SCENARIOS).nullable().default(null),
  multiplier: z.string().max(12).regex(SEASONAL_MULTIPLIER_PATTERN).default("1.00"),
  assumptionSource: z.enum(SEASONAL_ASSUMPTION_SOURCES).default("none"),
  lowerPercent: z.number().min(-90).max(300).nullable().default(null),
  expectedPercent: z.number().min(-90).max(300).nullable().default(null),
  upperPercent: z.number().min(-90).max(300).nullable().default(null),
}).strict();
export type SeasonalDemand = z.infer<typeof SeasonalDemandSchema>;

/**
 * The NL-014 calculation receipt, mirrored structurally so the planner state
 * validates it without importing the contract module. It carries provenance
 * hashes and contract versions only - never raw money, demand, or personal
 * values.
 */
export const FunnelCalculationComponentReferenceSchema = z.object({
  status: z.enum(["not-used", "used"]),
  contractVersion: z.string().max(80).nullable(),
  receiptHash: z.string().max(160).nullable(),
}).strict();

export const DEFAULT_CALCULATION_RECEIPT = null;

export const CalculationReceiptSchema = z.object({
  classification: z.literal("planning-estimate"),
  funnelStateVersion: FunnelStateVersionSchema,
  decimalRoundingContractVersion: z.literal(DECIMAL_ROUNDING_CONTRACT_VERSION),
  outputScale: z.number().int().min(0).max(18),
  calculatorVersion: z.string().max(80),
  formulaVersion: z.string().max(80),
  calculationTimestamp: z.string().max(40),
  evidenceDate: z.string().max(40),
  inputsHash: z.string().max(160),
  outputsHash: z.string().max(160),
  result: z.object({
    resultHash: z.string().max(160),
    unit: z.string().max(80),
  }).strict(),
  components: z.object({
    seasonalDemand: FunnelCalculationComponentReferenceSchema,
    priceEvidence: FunnelCalculationComponentReferenceSchema,
    packageRecommendation: FunnelCalculationComponentReferenceSchema,
  }).strict(),
}).strict().nullable();
export type CalculationReceipt = z.infer<typeof CalculationReceiptSchema>;

// Wizard State
export const FunnelWizardStateSchema = z.object({
  step: z.union([
    z.literal(1), z.literal(2), z.literal(3),
    z.literal(4), z.literal(5), z.literal(6), z.literal(7),
  ]),
  templateId: z.enum(FUNNEL_TEMPLATE_IDS).nullable(),
  audience: z.enum(["agency", "franchise", "creator", "adult", "enterprise"]).nullable(),
  goal: z.string().max(280).default(""),
  regionalSettings: RegionalSettingsSchema.default(DEFAULT_REGIONAL_SETTINGS),
  audienceGoalDetails: AudienceGoalDetailsSchema.default({}),
  trafficChannels: z.array(TrafficChannelSchema).min(0).max(12).default([]),
  stages: z.array(FunnelStageConfigSchema).min(0).max(12),
  graph: FunnelGraphSchema.optional(),
  products: z.array(ProductSchema).min(0).max(8).default([]),
  capacityConfig: CapacityConfigSchema,
  projectionInputs: ProjectionInputsSchema,
  expenses: z.array(FunnelExpenseSchema).min(0).max(32).default([]),
  scenarios: z.array(ScenarioSnapshotSchema).min(0).max(8).default([]),
  experiments: z.array(ExperimentSchema).min(0).max(12).default([]),
  packageRecommendation: PackageRecommendationSchema.default(DEFAULT_PACKAGE_RECOMMENDATION),
  seasonalDemand: SeasonalDemandSchema.default(DEFAULT_SEASONAL_DEMAND),
  calculationReceipt: CalculationReceiptSchema.default(DEFAULT_CALCULATION_RECEIPT),
  contact: FunnelContactSchema.nullable(),
  fitQuizBand: z.enum(["starter", "growth", "enterprise"]).nullable(),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  version: z.literal(CURRENT_VERSION).default(CURRENT_VERSION),
});
export type FunnelWizardState = z.infer<typeof FunnelWizardStateSchema>;

// Submitted State (for PDF endpoint)
export const FunnelSubmittedStateSchema = FunnelWizardStateSchema.extend({
  step: z.literal(7),
  templateId: z.enum(FUNNEL_TEMPLATE_IDS),
  audience: z.enum(["agency", "franchise", "creator", "adult", "enterprise"]),
  stages: z.array(FunnelStageConfigSchema).min(2).max(12),
  products: z.array(ProductSchema).min(0).max(8),
  contact: FunnelContactSchema,
});
export type FunnelSubmittedState = z.infer<typeof FunnelSubmittedStateSchema>;

export const FunnelPdfRequestSchema = z.object({
  wizardState: FunnelSubmittedStateSchema,
  locale: z.enum(ALL_APP_LOCALES),
  generatedAt: z.string().datetime().optional(),
}).strict();
export type FunnelPdfRequest = z.infer<typeof FunnelPdfRequestSchema>;

export type DraftSaveResult = "ok" | "quota_exceeded" | "unavailable";

export type DraftLoadResult =
  | { state: FunnelWizardState; discarded: false }
  | { state: null; discarded: true }
  | { state: null; discarded: false };
